import { useState } from 'react'
import DOMPurify from 'dompurify'
import { FileJson } from 'lucide-react'
import { Button } from '../../school_panel/components/ui'

interface Props {
    onExtract: (questions: any[]) => void;
}

// Helper: try to pull a string field from several possible keys
const pick = (obj: any, keys: string[]) => {
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k]
    }
    return undefined
}

const normalizeEntry = (entry: any) => {
    // entry may be either the fields object itself, or a wrapper { model, pk, fields }
    const fields = entry && typeof entry === 'object' && entry.fields ? entry.fields : entry
    if (!fields || typeof fields !== 'object') return null

    const question = pick(fields, ['ques', 'question', 'q', 'body', 'text'])
    const note = pick(fields, ['note', 'notes', 'note_text'])
    const choice_a = pick(fields, ['choice_a', 'choiceA', 'option1', 'option_1', 'option_01'])
    const choice_b = pick(fields, ['choice_b', 'choiceB', 'option2', 'option_2', 'option_02'])
    const choice_c = pick(fields, ['choice_c', 'choiceC', 'option3', 'option_3', 'option_03'])
    const choice_d = pick(fields, ['choice_d', 'choiceD', 'option4', 'option_4', 'option_04'])

    const ans = pick(fields, ['ans', 'answer', 'correct_ans', 'correct_answer', 'correct'])

    // If choices are stored in an array like options: [..]
    if ((!choice_a && Array.isArray(fields.options) && fields.options.length >= 4)) {
        const opts = fields.options
        return {
            question: question || '',
            choice_a: opts[0] || '',
            choice_b: opts[1] || '',
            choice_c: opts[2] || '',
            choice_d: opts[3] || '',
            ans: ans || ''
        }
    }

    // If choices are in keys like option_1..4 or opt1..4 they are already covered above

    // If minimal data missing (no question and no note), skip
    if (!question && !note) return null

    const finalAns = String(ans || '').trim()
    // Merge note into question text when available
    const questionText = (() => {
        const q = String(question || '').trim()
        const n = String(note || '').trim()
        if (n && q) return `${n} ${q}`
        if (n) return n
        return q
    })()

    return {
        question: questionText,
        choice_a: String(choice_a || '').trim(),
        choice_b: String(choice_b || '').trim(),
        choice_c: String(choice_c || '').trim(),
        choice_d: String(choice_d || '').trim(),
        ans: finalAns,
        // also include `answer` property which the Add Questions UI expects
        answer: finalAns
    }
}

const ExtractQuestionsFromJSON = ({ onExtract }: Props) => {
    const [fileText, setFileText] = useState<string>('')
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [reading, setReading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [previewCount, setPreviewCount] = useState<number | null>(null)
    const [lastParsed, setLastParsed] = useState<any[] | null>(null)

    const handleParse = () => {
        setError(null)
        setPreviewCount(null)
        setLastParsed(null)

        if (!fileText || fileText.trim() === '') {
            setError('Select a JSON file first.')
            return
        }

        let parsed: any
        try {
            parsed = JSON.parse(fileText)
        } catch (e) {
            setError('Invalid JSON: ' + (e instanceof Error ? e.message : String(e)))
            return
        }

        // If the top-level is an object with a "objects" or "data" array, try those
        if (parsed && typeof parsed === 'object') {
            if (!Array.isArray(parsed)) {
                if (Array.isArray(parsed.objects)) parsed = parsed.objects
                else if (Array.isArray(parsed.data)) parsed = parsed.data
                else {
                    // Attempt to find first array value inside
                    const values = Object.values(parsed).find(v => Array.isArray(v))
                    if (values) parsed = values as any
                }
            }
        }

        if (!Array.isArray(parsed)) {
            setError('Expected a JSON array of questions or Django dump objects.')
            return
        }

        const out: any[] = []
        for (const item of parsed) {
            const norm = normalizeEntry(item)
            if (norm) out.push(norm)
        }

        if (out.length === 0) {
            setError('No valid questions found. Each entry needs a "fields" property or question and choice keys.')
            return
        }

        setPreviewCount(out.length)
        setLastParsed(out)
    }

    const handleAccept = () => {
        if (!lastParsed || lastParsed.length === 0) {
            setError('Parse the file before accepting the questions.')
            return
        }
        // call onExtract with normalized objects
        onExtract(lastParsed)
    }

    const handleClear = () => {
        // clear file and parsed state
        setSelectedFile(null)
        setFileText('')
        setError(null)
        setPreviewCount(null)
        setLastParsed(null)
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files && e.target.files[0]
        if (!f) {
            setSelectedFile(null)
            setFileText('')
            return
        }
        setSelectedFile(f)
        setReading(true)
        const reader = new FileReader()
        reader.onload = (ev) => {
            const result = ev.target?.result
            setFileText(result ? String(result) : '')
            setReading(false)
        }
        reader.onerror = (ev) => {
            setReading(false)
            setError('The file could not be read.')
            setFileText('')
            setSelectedFile(null)
        }
        reader.readAsText(f)
    }

    return (
        <div>
            <div className='rounded-[18px] border-2 border-dashed border-line-strong bg-surface-2 px-5 py-8 text-center'>
                <span aria-hidden className='mx-auto mb-3 grid h-[52px] w-[52px] place-items-center rounded-[14px] bg-mist text-slate'><FileJson size={22} strokeWidth={1.8} /></span>
                <h3 className='font-display text-[17px] font-bold text-ink'>Extract from JSON</h3>
                <p className='mx-auto mt-1.5 max-w-[60ch] text-[13px] text-muted'>Select a JSON file (Django dump "objects" or a plain array). The extractor will look for fields.ques, choice_a..choice_d and ans (or common variants).</p>
                <input aria-label='Select JSON file' title='Select JSON file' type='file' accept='.json,application/json' onChange={handleFileChange}
                    className='mx-auto mt-4 block w-full max-w-[420px] cursor-pointer text-sm text-muted file:mr-3 file:min-h-[40px] file:cursor-pointer file:rounded-[12px] file:border-0 file:bg-navy file:px-4 file:text-sm file:font-semibold file:text-white hover:file:bg-navy-2' />
                {selectedFile && <div className='mt-2 break-words text-[13px] text-muted'>Selected file: <span className='font-semibold text-ink'>{selectedFile.name}</span></div>}
            </div>
            <div className='mt-4 flex flex-wrap items-center gap-2'>
                <Button variant="secondary" onClick={handleParse} disabled={reading}>Parse JSON</Button>
                <Button variant="ghost" onClick={handleClear}>Clear</Button>
                <div className='flex-1' />
                {previewCount !== null && (
                    <div className='flex flex-wrap items-center gap-2 text-sm text-muted'>
                        Parsed {previewCount} {previewCount === 1 ? 'question' : 'questions'}.
                        <Button size="sm" onClick={handleAccept}>Use these questions</Button>
                    </div>
                )}
            </div>
            {error && <p role="alert" className='mt-3 rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink'>{error}</p>}
            {lastParsed && lastParsed.length > 0 && (
                <div className='mt-4'>
                    <h3 className='font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted'>Preview (first 5)</h3>
                    <div className='mt-2 space-y-2.5'>
                        {lastParsed.slice(0, 5).map((q, idx) => {
                            const normalize = (s: any) => (s === null || s === undefined) ? '' : String(s).replace(/\s+/g, ' ').trim().toLowerCase()
                            const ansRaw = String(q.ans || '').trim()
                            const ansUpper = ansRaw.toUpperCase()
                            const labelMap: Record<string, number> = { 'A': 0, 'B': 1, 'C': 2, 'D': 3 }
                            let ansIndex = -1
                            if (labelMap.hasOwnProperty(ansUpper)) {
                                ansIndex = labelMap[ansUpper]
                            } else if (/^[1-4]$/.test(ansRaw)) {
                                ansIndex = parseInt(ansRaw, 10) - 1
                            } else {
                                const choices = [q.choice_a, q.choice_b, q.choice_c, q.choice_d].map(normalize)
                                const normAns = normalize(ansRaw)
                                const found = choices.findIndex(c => c !== '' && c === normAns)
                                if (found >= 0) ansIndex = found
                            }

                            const choiceItems = [
                                { label: 'A', text: q.choice_a },
                                { label: 'B', text: q.choice_b },
                                { label: 'C', text: q.choice_c },
                                { label: 'D', text: q.choice_d },
                            ]

                            return (
                                <div key={idx} className='rounded-[14px] border border-line bg-surface p-3.5'>
                                    <div className='mb-2 break-words font-medium text-ink'><span className='mr-1 font-mono text-xs text-muted'>Q{idx + 1}</span> <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(q.question)) }} /></div>
                                    <div className='grid grid-cols-1 gap-1.5 sm:grid-cols-2'>
                                        {choiceItems.map((it, i) => (
                                            <div key={i} className={`min-w-0 break-words rounded-[10px] border px-2.5 py-1.5 text-[13px] text-ink ${i === ansIndex ? 'border-success/40 bg-success-bg font-medium' : 'border-line'}`}>
                                                <span className='mr-2 font-mono font-semibold'>{it.label}</span>
                                                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(it.text || '')) }} />
                                            </div>
                                        ))}
                                    </div>
                                    <div className='mt-2 text-[13px] text-muted'>Answer: <span className='font-mono font-semibold text-ink'>{String(q.ans)}</span></div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ExtractQuestionsFromJSON
